import {
  Experience,
  Project,
  AIProject,
  Skill,
  Education,
  Certification,
  ContactInfo,
  OtherInterests,
} from "../types";

export const experiences: Experience[] = [
  {
    id: "1",
    title: "IT Architect / Infrastructure Architect",
    company: "CGI",
    location: "Stockholm, Sweden",
    duration: "2026 - Present",
    description:
      "Pre-sales design authority for secure, enterprise-grade AI platforms and replicable cloud reference architectures.",
    achievements: [
      "Author secure AI platform architectures and functional PoCs covering network segmentation, Private Endpoints, and WAF",
      "Define Microsoft Entra ID, Managed Identity, and RBAC governance for least-privilege partner and client integrations",
      "Architect secure integration flows between LibreChat, FastMCP, cloud services, and enterprise resources",
      "Deflect delivery risk through deployment sequencing, monitoring, logging, and certificate lifecycle planning",
    ],
    technologies: [
      "Azure",
      "Microsoft Entra ID",
      "Managed Identities",
      "RBAC",
      "Private Endpoints",
      "WAF",
      "AI Platform Architecture",
      "LibreChat",
      "FastMCP",
    ],
  },
  {
    id: "2",
    title: "Solutions Architect",
    company: "Sogeti",
    location: "Stockholm, Sweden",
    duration: "2023 - 2025",
    description:
      "Lead Solution Architect and trusted technical advisor for regional, financial, industrial, and energy clients.",
    achievements: [
      "Influenced five key stakeholders to adopt Azure for a large regional digital initiative through cost, delivery, and governance analysis",
      "Led architecture workshops and executive discussions to align hybrid cloud strategy with regulatory requirements",
      "Raised identity protection compliance from 21% to 92% for a major Nordic bank through MFA, encryption, and private networking guidance",
      "Directed secure multi-environment infrastructure and PoC architecture for Sandvik and Stockholm Exergi",
    ],
    technologies: [
      "Azure",
      "Microsoft Entra ID",
      "MFA",
      "Private Networking",
      "Azure IoT",
      "ARM Templates",
      "Azure DevOps",
      "Hybrid Cloud",
      "Azure IoT Hub",
    ],
  },
  {
    id: "3",
    title: "Sr. Software Developer & Assistant Architect",
    company: "Ericsson",
    location: "Sweden",
    duration: "2020 - 2022",
    description:
      "Modernized enterprise scheduling and messaging workloads by advocating cloud-native Azure services.",
    achievements: [
      "Proposed Azure Functions and Azure Service Bus as cloud-native alternatives to Windows Scheduler and RabbitMQ",
      "Influenced architects, product owners, and technical stakeholders to adopt the proposed Azure services across environments",
      "Translated modernization benefits into reduced operational overhead, simpler deployments, and improved scalability",
    ],
    technologies: [
      "Azure Functions",
      "Azure Service Bus",
      ".NET",
      "RabbitMQ",
      "Cloud-Native Architecture",
    ],
  },
  {
    id: "4",
    title: "Sr. Application Developer",
    company: "Volvo Trucks",
    location: "Skövde, Sweden",
    duration: "2012 - 2020",
    description:
      "Designed integration APIs and hybrid applications for global manufacturing, demand planning, and robotics workflows.",
    achievements: [
      "Built integration APIs bridging AWS-hosted cloud services with on-premises databases and autonomous robotics workflows",
      "Delivered critical Demand and Supply Planning Tools that calculated global demand without legacy SAP transfers",
      "Developed Factory Control System interfaces used in production facilities across New River Valley, TUVE, Japan, and Russia",
    ],
    technologies: [
      "AWS",
      ".NET",
      "C#",
      "REST APIs",
      "On-Premises Integration",
      "Autonomous Robotics",
      "SQL Server",
    ],
  },
  {
    id: "5",
    title: "Architect / C.E.O",
    company: "Riy Technologies",
    location: "Skövde, Sweden",
    duration: "2011 - 2020",
    description:
      "Built and led a technology company delivering resilient event-management solutions for the sports industry.",
    achievements: [
      "Expanded the client base from 5 to more than 100 enterprise clients within 12 months through ecosystem partnerships",
      "Designed an event-driven Event Management System supporting more than 200,000 live participants",
      "Led architecture, delivery, and offshore engineering for highly concurrent sports events",
    ],
    technologies: [
      "Event-Driven Architecture",
      "RabbitMQ",
      "Scalable Data Stores",
      ".NET",
      "C#",
      "Python",
    ],
  },
];

export const projects: Project[] = [
  {
    id: "1",
    title: "KliPP/Placement Portal",
    description:
      "Led design and delivery of cloud-based SaaS Placement Portal for Region Stockholm as Lead Solution Architect, serving as critical digital enabler in the region's education ecosystem.",
    technologies: [
      "Azure",
      "Plant UML",
      "Azure DevOps",
      ".NET Core 8",
      "EF Core 8",
      "React JS",
      "SASS",
      "PrimeReact",
    ],
    features: [
      "Full architectural design and team leadership (8-10 developers)",
      "Modern Azure-based tech stack implementation",
      "Compliance and security requirements management",
      "Stakeholder expectation management and budget forecasting",
    ],
  },
  {
    id: "2",
    title: "Nordic Bank Security Infrastructure",
    description:
      "Served as Azure Solution Architect for one of the largest Nordic banks, implementing comprehensive security updates to cloud infrastructure.",
    technologies: [
      "Azure Entra ID",
      "Azure VMs",
      "Storage",
      "Networks",
      "Private Endpoints",
      "MS WAF",
    ],
    features: [
      "Implemented MFA and encryption solutions",
      "Updated Azure Entra ID configurations",
      "Raised identity protection compliance from 21% to 92%",
      "Collaborated with InfoSec team and global IT governance",
    ],
  },
  {
    id: "3",
    title: "Sandvik Data Integration Infrastructure",
    description:
      "Led architectural design and deployment of multi-environment cloud infrastructure for enterprise-wide data integration at Sandvik.",
    technologies: [
      "Azure Entra ID",
      "Data Factory",
      "SQL Server",
      "Power BI",
      "VNets",
      "Managed Identities",
    ],
    features: [
      "Created ARM templates for consistent provisioning",
      "Developed security blueprints using Well-Architected Framework",
      "Enabled automated, secure, and scalable data integration",
      "Supported Sandvik's ongoing cloud transformation",
    ],
  },
  {
    id: "4",
    title: "ECOD Platform Development",
    description:
      "Led development of ECOD application at Ericsson, implementing new features, performance optimizations, and critical bug fixes.",
    technologies: [
      ".NET Core 3.1",
      "REST API",
      "Entity Framework 3.0",
      "ReactJS",
      "TypeScript",
      "SQL Server",
      "Azure",
    ],
    features: [
      "Automatic User Registration system",
      "SAP Data Synchronization without user interruption",
      "Custom React components for week/month/quarter selection",
      "Excel import for unstructured and localized files",
    ],
  },
  {
    id: "5",
    title: "Factory Control System (FCS)",
    description:
      "Developed comprehensive factory control system for Volvo Trucks, used in major production facilities globally including New River Valley, TUVE, Japan, and Russia.",
    technologies: [
      ".NET C# 3",
      "MVC 3",
      "KnockoutJS",
      "Kendo UI",
      "jQuery",
      "SignalR",
      "SQL Server",
      "nHibernate",
    ],
    features: [
      "Delivered 90% of FCS user interfaces within 2 years",
      "Custom jQuery components used throughout application",
      "Backend development and database query optimization",
      "Integration with factory automation systems",
    ],
  },
  {
    id: "6",
    title: "Event Management System (EMS)",
    description:
      "Architected and developed end-to-end Event Management System for sports industry, supporting vendor/attendee management, payments, and marketing.",
    technologies: [
      ".NET C# 5/6/Core",
      "MVC 5",
      "Angular",
      "React",
      "VueJS",
      "MySQL",
      "Web3",
      "Solidity",
      "Python",
    ],
    features: [
      "Event-based architecture for high traffic scalability",
      "Vendor and attendee management systems",
      "Online payment integration",
      "Email marketing and user management",
    ],
  },
];

export const aiProjects: AIProject[] = [
  {
    id: "ai-1",
    title: "My AI Avatar",
    description:
      "A Retrieval-Augmented Generation solution that combines context management, knowledge retrieval, and an LLM interface to answer questions about my professional background.",
    icon: "fas fa-user-astronaut",
    technologies: [
      "RAG",
      "LLM Integration",
      "N8N",
      "REST APIs",
      "Knowledge Retrieval",
    ],
    features: [
      "Context-aware professional knowledge retrieval",
      "Conversational interface for portfolio visitors",
      "Session-aware question handling",
      "Continuous self-directed AI experimentation",
    ],
    capabilities: [
      "Retrieval-Augmented Generation",
      "Context Management",
      "Knowledge Retrieval",
      "LLM Application Design",
    ],
  },
  {
    id: "ai-2",
    title: "Secure AI Platform Architecture",
    description:
      "Enterprise AI platform architecture focused on secure integrations, private connectivity, identity governance, and delivery-ready reference designs.",
    icon: "fas fa-brain",
    technologies: ["Azure", "Microsoft Entra ID", "Private Endpoints", "Managed Identities", "RBAC", "WAF", "FastMCP"],
    features: [
      "Secure AI platform reference architecture",
      "Network segmentation and private service connectivity",
      "Least-privilege identity and access governance",
      "Technical PoCs and partner implementation playbooks",
    ],
    capabilities: [
      "Zero Trust Architecture",
      "Cloud Security Governance",
      "Hybrid Connectivity",
      "Partner Enablement",
    ],
  },
];

export const skills: Skill[] = [
  // Cloud & Infrastructure
  {
    id: "1",
    name: "Azure DevOps",
    icon: "fab fa-microsoft",
    category: "cloud",
  },
  {
    id: "2",
    name: "Azure Infrastructure",
    icon: "fab fa-microsoft",
    category: "cloud",
  },
  { id: "3", name: "Azure IoT", icon: "fas fa-cloud", category: "cloud" },
  { id: "4", name: "AWS", icon: "fab fa-aws", category: "cloud" },
  { id: "5", name: "Docker", icon: "fab fa-docker", category: "cloud" },
  { id: "25", name: "Kubernetes", icon: "fas fa-dharmachakra", category: "cloud" },
  { id: "26", name: "Terraform", icon: "fas fa-code-branch", category: "cloud" },
  { id: "27", name: "Azure Container Apps", icon: "fas fa-cubes", category: "cloud" },
  { id: "6", name: "CI/CD", icon: "fas fa-cogs", category: "tools" },

  // Backend Technologies
  { id: "7", name: "C#", icon: "fab fa-microsoft", category: "backend" },
  { id: "8", name: ".NET Core 8", icon: "fas fa-code", category: "backend" },
  {
    id: "9",
    name: "Entity Framework",
    icon: "fas fa-database",
    category: "backend",
  },
  { id: "10", name: "REST APIs", icon: "fas fa-server", category: "backend" },
  {
    id: "11",
    name: "Microservices",
    icon: "fas fa-cubes",
    category: "backend",
  },
  { id: "12", name: "Node.js", icon: "fab fa-node-js", category: "backend" },
  { id: "28", name: "Python", icon: "fab fa-python", category: "backend" },
  { id: "29", name: "OpenAPI", icon: "fas fa-file-code", category: "backend" },

  // Frontend Technologies
  { id: "13", name: "ReactJS 18", icon: "fab fa-react", category: "frontend" },
  {
    id: "14",
    name: "Angular 11",
    icon: "fab fa-angular",
    category: "frontend",
  },
  {
    id: "15",
    name: "TypeScript",
    icon: "fab fa-js-square",
    category: "frontend",
  },
  { id: "16", name: "HTML5", icon: "fab fa-html5", category: "frontend" },
  {
    id: "17",
    name: "CSS3/SCSS",
    icon: "fab fa-css3-alt",
    category: "frontend",
  },
  { id: "18", name: "jQuery", icon: "fab fa-js-square", category: "frontend" },

  // Database & Data
  {
    id: "19",
    name: "SQL Server 2019",
    icon: "fas fa-database",
    category: "database",
  },
  { id: "20", name: "MySQL 5", icon: "fas fa-database", category: "database" },
  {
    id: "21",
    name: "Data Integration",
    icon: "fas fa-exchange-alt",
    category: "database",
  },
  { id: "30", name: "Cosmos DB", icon: "fas fa-database", category: "database" },
  { id: "31", name: "Vector Search", icon: "fas fa-search", category: "database" },

  // Tools & Methodologies
  {
    id: "22",
    name: "Agile/Scrum/Kanban",
    icon: "fas fa-tasks",
    category: "tools",
  },
  {
    id: "23",
    name: "Team Leadership",
    icon: "fas fa-users",
    category: "tools",
  },
  {
    id: "24",
    name: "Machine Learning",
    icon: "fas fa-brain",
    category: "tools",
  },
  { id: "32", name: "Microsoft Entra ID", icon: "fab fa-microsoft", category: "tools" },
  { id: "33", name: "RBAC", icon: "fas fa-user-shield", category: "tools" },
  { id: "34", name: "RAG", icon: "fas fa-brain", category: "tools" },
];

export const education: Education[] = [
  {
    id: "1",
    degree: "Master in Advanced Computer Graphics",
    institution: "Linköping University",
    location: "Norrköping, Sweden",
    duration: "2009 - 2011",
    description:
      "Studied computer graphics and programming. Major projects involved developing plugins using C# and Flash for statistics browser, game development using Unity3D, and 3D effects using Houdini.",
    icon: "fas fa-graduation-cap",
  },
  {
    id: "2",
    degree: "Bachelor of Computer in Information Technology",
    institution: "Preston University",
    location: "Pakistan",
    duration: "1999 - 2003",
    description:
      "Studied basics of computer programming using C/C++, Pascal, Visual Basic, and ASP, as well as 3Ds Max, Maya, and After Effects. Major project: School Management System with student registration, teacher attendance, schedule management, and account management.",
    icon: "fas fa-university",
  },
];

export const certifications: Certification[] = [
  {
    id: "1",
    name: "AWS Solution Architect Professional",
    issuer: "(2022-10)",
    isCertified: false,
  },
  {
    id: "2",
    name: "Azure Solution Architect Professional",
    issuer: "(2022-03)",
    isCertified: false,
  },
  {
    id: "3",
    name: "Azure Fundamentals",
    issuer: "(2021-09)",
    isCertified: false,
  },
  {
    id: "4",
    name: "Machine Learning",
    issuer: "(2021-06)",
    isCertified: false,
  },
    { id: "5", name: "PRINCE2", issuer: "(2016-05)", isCertified: false },
    {
      id: "6",
      name: "AI Engineer Agentic Track",
      issuer: "(2025-12)",
      isCertified: false,
    },
];

export const contactInfo: ContactInfo = {
  location: "Sweden",
  linkedin: "https://linkedin.com/in/riyasat-ali",
  github: "https://github.com/riyasat",
};

export const otherInterests: OtherInterests[] = [
  {
    id: "1",
    name: "Photography",
    description:
      "Capturing moments through the lens, experimenting with light, composition, and storytelling to create timeless memories.",
  },
  {
    id: "2",
    name: "Travel",
    description:
      "Exploring new cultures, cuisines, and landscapes while embracing diverse perspectives and unique experiences.",
  },
  {
    id: "3",
    name: "Gaming",
    description:
      "Immersing in virtual worlds that challenge creativity, strategy, and teamwork across different genres.",
  },
  {
    id: "4",
    name: "Table Tennis",
    description:
      "Enjoy playing table tennis both recreationally and competitively, improving reflexes, focus, and quick decision-making.",
  },
];